<template>
  <b-modal
    v-model="show"
    :title="form.id ? 'Edit Package' : 'New Package'"
    size="xl"
    ok-title="Save"
    :ok-disabled="saving"
    no-close-on-backdrop
    dialog-class="package-editor-dialog"
    @ok="handleSave"
  >
    <b-overlay :show="saving">
      <b-form @submit.prevent>
        <b-row>
          <!-- Editor column -->
          <b-col cols="12" lg="8">
            <b-row>
              <b-col cols="12" md="5">
                <b-form-group label="Name">
                  <b-form-input v-model="form.name" placeholder="e.g. Corporate client standard pack" autofocus />
                </b-form-group>
              </b-col>
              <b-col cols="12" md="4">
                <b-form-group label="Code">
                  <b-form-input v-model="form.code" placeholder="e.g. CORP_STD" />
                  <small class="text-muted">Optional, generated from the name if empty. Used in exports and logs.</small>
                </b-form-group>
              </b-col>
              <b-col cols="12" md="3">
                <b-form-group label="Order">
                  <b-form-input v-model.number="form.sortOrder" type="number" min="0" step="1" />
                  <small class="text-muted">Packages are applied in ascending order.</small>
                </b-form-group>
              </b-col>
            </b-row>

            <b-form-group label="Description">
              <b-form-textarea v-model="form.description" rows="2" placeholder="Shown to administrators only" />
            </b-form-group>

            <b-form-group>
              <b-form-checkbox v-model="form.isDefault" switch>
                Default package
              </b-form-checkbox>
              <small class="text-muted">
                Applied automatically to accounts matching the selectors below; leave a selector empty for any.
              </small>
            </b-form-group>

            <b-row v-if="form.isDefault" class="mb-1">
              <b-col cols="12" md="4">
                <b-form-group label="Client types">
                  <b-form-checkbox-group v-model="form.clientTypes" :options="data.clientTypes || []" stacked />
                </b-form-group>
              </b-col>
              <b-col cols="12" md="4">
                <b-form-group label="Account types">
                  <b-form-checkbox-group v-model="form.accountTypes" :options="data.accountTypes || []" stacked />
                </b-form-group>
              </b-col>
              <b-col cols="12" md="4">
                <b-form-group label="Client categories">
                  <b-form-checkbox-group v-model="form.clientCategories" :options="data.clientCategories || []" stacked />
                </b-form-group>
              </b-col>
            </b-row>

            <hr />

            <div class="d-flex justify-content-between align-items-center mb-25">
              <h5 class="mb-0">Package items by section</h5>
              <small class="text-muted">{{ totalItems }} item{{ totalItems === 1 ? '' : 's' }}</small>
            </div>
            <small class="text-muted d-block mb-1">
              Items appear on the client form in the order below. Person questions and uploads are asked of
              each person listed in that section.
            </small>

            <div
              v-for="group in groups"
              :key="group.key"
              class="group-card mb-75"
              :class="{ 'group-card-declaration': group.isDeclaration }"
            >
              <div class="group-head" @click="toggleGroup(group.key)">
                <feather-icon :icon="isOpen(group.key) ? 'ChevronDownIcon' : 'ChevronRightIcon'" size="16" class="mr-50" />
                <code v-if="group.sectionKey" class="mr-50">{{ group.sectionKey }}</code>
                <feather-icon v-if="group.personType" icon="UsersIcon" size="14" class="mr-25 text-primary" />
                <strong class="mr-50">{{ group.title }}</strong>
                <b-badge variant="light-secondary" class="mr-auto">
                  {{ itemsOf(group.key).length }}
                </b-badge>
                <div v-if="!group.isDeclaration" class="group-actions" @click.stop>
                  <b-button
                    v-if="group.allows.question"
                    size="sm"
                    variant="outline-primary"
                    class="ml-25"
                    @click="startPicker(group, 'Question')"
                  >
                    <feather-icon icon="PlusIcon" size="12" /> Add question
                  </b-button>
                  <b-button
                    v-if="group.allows.notice"
                    size="sm"
                    variant="outline-warning"
                    class="ml-25"
                    @click="startPicker(group, 'Notice')"
                  >
                    <feather-icon icon="PlusIcon" size="12" /> Add notice
                  </b-button>
                  <b-button
                    v-if="group.allows.upload"
                    size="sm"
                    variant="outline-info"
                    class="ml-25"
                    @click="startPicker(group, 'Upload')"
                  >
                    <feather-icon icon="PlusIcon" size="12" /> Add upload
                  </b-button>
                </div>
              </div>

              <b-collapse :visible="isOpen(group.key)">
                <div class="group-body">
                  <small v-if="group.subtitle" class="text-muted d-block mb-50">{{ group.subtitle }}</small>
                  <div v-if="group.isDeclaration" class="text-muted small">
                    <feather-icon icon="LockIcon" size="12" class="mr-25" />
                    The Client Declaration is always included on the Review &amp; sign step.
                  </div>

                  <div v-if="!itemsOf(group.key).length && !group.isDeclaration && !isPicking(group.key)" class="text-muted small py-25">
                    No items in this section yet.
                  </div>

                  <div
                    v-for="(item, index) in itemsOf(group.key)"
                    :key="item.uid"
                    class="item-row"
                  >
                    <div class="item-main">
                      <b-badge :variant="kindBadge(item.itemKind)" class="mr-50">{{ kindLabel(item.itemKind) }}</b-badge>
                      <span class="item-label">{{ itemLabel(item) }}</span>
                      <small class="text-muted ml-50">{{ itemMeta(item) }}</small>
                    </div>
                    <div class="item-note">
                      <b-form-input v-model="item.note" size="sm" placeholder="Instruction shown to the client" />
                    </div>
                    <div class="item-required">
                      <b-form-checkbox v-if="item.itemKind !== 'Notice'" v-model="item.required" switch size="sm">
                        <small>Required</small>
                      </b-form-checkbox>
                    </div>
                    <div class="item-actions text-nowrap">
                      <b-button size="sm" variant="flat-secondary" class="btn-icon" title="Move up" :disabled="index === 0" @click="moveItem(group.key, index, -1)">
                        <feather-icon icon="ArrowUpIcon" />
                      </b-button>
                      <b-button size="sm" variant="flat-secondary" class="btn-icon" title="Move down" :disabled="index === itemsOf(group.key).length - 1" @click="moveItem(group.key, index, 1)">
                        <feather-icon icon="ArrowDownIcon" />
                      </b-button>
                      <b-button size="sm" variant="flat-danger" class="btn-icon" title="Remove" @click="removeItem(group.key, index)">
                        <feather-icon icon="XIcon" />
                      </b-button>
                    </div>
                  </div>

                  <!-- Inline picker -->
                  <div v-if="isPicking(group.key)" class="picker-row">
                    <template v-if="picker.kind === 'Question'">
                      <v-select
                        v-model="picker.value"
                        :options="questionChoices(group)"
                        label="Label"
                        placeholder="Pick a question"
                        class="picker-select"
                        @input="onPickQuestion(group, $event)"
                      >
                        <template #option="q">
                          <template v-if="q.id === NEW_ID">
                            <feather-icon icon="PlusCircleIcon" size="14" class="mr-25 text-primary" />
                            <span class="text-primary">Create new question…</span>
                          </template>
                          <template v-else>
                            {{ q.Label }}
                            <small class="text-muted ml-25">{{ q.Key }} · {{ q.InputType }}</small>
                          </template>
                        </template>
                        <template #no-options>No more questions for this scope. Create a new one.</template>
                      </v-select>
                    </template>

                    <template v-else-if="picker.kind === 'Notice'">
                      <v-select
                        v-model="picker.value"
                        :options="noticeChoices()"
                        label="Title"
                        placeholder="Pick a notice"
                        class="picker-select"
                        @input="onPickNotice(group, $event)"
                      >
                        <template #option="n">
                          <template v-if="n.id === NEW_ID">
                            <feather-icon icon="PlusCircleIcon" size="14" class="mr-25 text-primary" />
                            <span class="text-primary">Create new notice…</span>
                          </template>
                          <template v-else>
                            {{ n.Title }}
                            <small class="text-muted ml-25">{{ n.Key }} · {{ ackLabel(n.AckMode) }}</small>
                          </template>
                        </template>
                        <template #no-options>No more notices. Create a new one.</template>
                      </v-select>
                    </template>

                    <template v-else>
                      <v-select
                        v-model="picker.documentType"
                        :options="data.documentTypes || []"
                        label="Name"
                        taggable
                        :create-option="createDocumentTypeOption"
                        placeholder="Pick a document or type a new name"
                        class="picker-select"
                      />
                      <b-form-select
                        v-model="picker.scope"
                        :options="scopeOptions"
                        size="sm"
                        class="picker-scope"
                        :disabled="!!group.personType"
                      />
                      <b-form-checkbox v-model="picker.required" class="picker-required">
                        <small>Required</small>
                      </b-form-checkbox>
                      <b-button size="sm" variant="primary" @click="addUpload(group)">Add</b-button>
                    </template>

                    <b-button size="sm" variant="flat-secondary" class="btn-icon ml-25" title="Cancel" @click="cancelPicker">
                      <feather-icon icon="XIcon" />
                    </b-button>
                  </div>
                </div>
              </b-collapse>
            </div>

            <hr />

            <b-form-group v-if="form.id" label="Apply this change to">
              <b-form-radio-group v-model="form.applyToExisting" stacked>
                <b-form-radio :value="false">
                  New onboardings only
                  <small class="text-muted d-block">
                    Onboardings that already have this package keep the items they were started with.
                  </small>
                </b-form-radio>
                <b-form-radio :value="true">
                  Also the {{ form.appliedCount }} existing onboarding(s) that have this package
                  <small class="text-muted d-block">
                    New items are added and removed items withdrawn on those onboardings,
                    including forms clients have already signed. Uploaded documents are kept.
                  </small>
                </b-form-radio>
              </b-form-radio-group>
            </b-form-group>
            <b-alert v-else show variant="info" class="p-1">
              A new default package is applied to onboardings opened from now on and to accounts
              whose type or category changes to match it. To give it to an onboarding already under
              way, add it from that onboarding's Packages tab.
            </b-alert>

            <b-form-group v-if="form.id && form.applyToExisting">
              <b-form-checkbox v-model="form.notify">
                Email affected clients about new items
              </b-form-checkbox>
              <small class="text-muted">
                Clients whose onboarding gains new questions, notices or document requests receive a notification.
              </small>
            </b-form-group>
          </b-col>

          <!-- Assembled form column -->
          <b-col cols="12" lg="4">
            <b-button
              variant="outline-secondary"
              size="sm"
              block
              class="d-lg-none mb-50"
              @click="showAssembled = !showAssembled"
            >
              <feather-icon :icon="showAssembled ? 'ChevronUpIcon' : 'ChevronDownIcon'" class="mr-25" />
              Assembled form
            </b-button>
            <div class="assembled-panel" :class="{ 'd-none d-lg-block': !showAssembled }">
              <h6 class="mb-25">Assembled form</h6>
              <small class="text-muted d-block mb-75">
                What this package adds, in form order. Required items are marked *.
              </small>
              <div v-for="step in assembled" :key="step.key" class="assembled-step">
                <div class="assembled-head">
                  <code v-if="step.sectionKey" class="mr-25">{{ step.sectionKey }}</code>
                  <feather-icon v-else :icon="step.icon" size="12" class="mr-25" />
                  <span>{{ step.title }}</span>
                </div>
                <ul class="assembled-items">
                  <li v-for="row in step.rows" :key="row.key">
                    <b-badge :variant="kindBadge(row.kind)" class="assembled-kind">{{ row.kind.charAt(0) }}</b-badge>
                    <span :class="{ 'text-muted': row.muted }">{{ row.label }}</span>
                    <span v-if="row.required" class="text-danger">*</span>
                    <small v-if="row.note" class="text-muted d-block">{{ row.note }}</small>
                  </li>
                </ul>
              </div>
              <div v-if="!totalItems" class="text-muted small">
                Nothing added yet. Every form still has the Review &amp; sign step with the Client Declaration.
              </div>
            </div>
          </b-col>
        </b-row>
      </b-form>
    </b-overlay>

    <question-modal ref="questionModal" :data="data" @saved="onQuestionCreated" />
    <notice-modal ref="noticeModal" :data="data" @saved="onNoticeCreated" />
  </b-modal>
</template>

<script>
import {
  BModal, BOverlay, BForm, BRow, BCol, BFormGroup, BFormInput, BFormTextarea, BFormCheckbox,
  BFormCheckboxGroup, BFormSelect, BFormRadio, BFormRadioGroup, BButton, BBadge, BAlert, BCollapse,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import store from '@/store'
import QuestionModal from './QuestionModal.vue'
import NoticeModal from './NoticeModal.vue'
import {
  apiError, kindBadge, kindLabel, ackLabel, scopeLabel, nextUid,
} from './builderHelpers'

const NEW_ID = '__new__'
// Pseudo group for account-level uploads that are not tied to a form section; they are
// requested on the Documents step of the client form.
const UPLOADS_KEY = '__uploads__'
const DECLARATION_KEY = 'E'

function emptyForm() {
  return {
    id: null,
    name: '',
    code: '',
    description: '',
    isDefault: false,
    clientTypes: [],
    accountTypes: [],
    clientCategories: [],
    sortOrder: 0,
    appliedCount: 0,
    applyToExisting: false,
    notify: false,
  }
}

function emptyPicker() {
  return {
    groupKey: null,
    kind: null,
    value: null,
    documentType: null,
    scope: 'Account',
    required: true,
  }
}

function newRow(kind) {
  return {
    uid: nextUid(),
    id: null,
    itemKind: kind,
    questionId: null,
    noticeId: null,
    documentType: null,
    scope: 'Account',
    required: true,
    note: '',
    // snapshot labels in case the catalogue row is missing (deleted meanwhile)
    label: '',
    meta: '',
  }
}

export default {
  name: 'PackageEditorModal',

  components: {
    BModal, BOverlay, BForm, BRow, BCol, BFormGroup, BFormInput, BFormTextarea, BFormCheckbox,
    BFormCheckboxGroup, BFormSelect, BFormRadio, BFormRadioGroup, BButton, BBadge, BAlert, BCollapse,
    vSelect, QuestionModal, NoticeModal,
  },

  props: {
    data: { type: Object, required: true },
  },

  data() {
    return {
      NEW_ID,
      show: false,
      saving: false,
      showAssembled: false,
      form: emptyForm(),
      // section key (or pseudo key) → item rows
      itemsBySection: {},
      openGroups: {},
      picker: emptyPicker(),
    }
  },

  computed: {
    questionsById() {
      const map = {}
      ;(this.data.questions || []).forEach(q => { map[q.id] = q })
      return map
    },
    noticesById() {
      const map = {}
      ;(this.data.notices || []).forEach(n => { map[n.id] = n })
      return map
    },
    sortedSections() {
      return [...(this.data.sections || [])].sort((a, b) => {
        const d = (Number(a.SortOrder) || 0) - (Number(b.SortOrder) || 0)
        return d !== 0 ? d : (a.id || 0) - (b.id || 0)
      })
    },
    groups() {
      const list = []
      let declaration = null
      this.sortedSections.forEach(s => {
        const group = {
          key: s.Key,
          sectionKey: s.Key,
          title: s.Title,
          subtitle: s.Subtitle,
          personType: s.PersonType || null,
          isSystem: !!s.IsSystem,
          isDeclaration: s.Key === DECLARATION_KEY,
          allows: s.PersonType
            ? { question: true, notice: false, upload: true }
            : { question: true, notice: true, upload: true },
        }
        if (group.isDeclaration) declaration = group
        else list.push(group)
      })
      list.push({
        key: UPLOADS_KEY,
        sectionKey: null,
        title: 'Documents',
        subtitle: 'Account-level uploads requested on the Documents step, not tied to a form section.',
        personType: null,
        isSystem: true,
        isDeclaration: false,
        allows: { question: false, notice: false, upload: true },
      })
      if (declaration) list.push(declaration)
      return list
    },
    totalItems() {
      return Object.keys(this.itemsBySection).reduce((n, k) => n + this.itemsBySection[k].length, 0)
    },
    scopeOptions() {
      return (this.data.scopes || []).map(s => ({ value: s.value, text: s.label }))
    },
    usedQuestionIds() {
      const ids = {}
      Object.keys(this.itemsBySection).forEach(k => {
        this.itemsBySection[k].forEach(it => { if (it.itemKind === 'Question' && it.questionId) ids[it.questionId] = true })
      })
      return ids
    },
    usedNoticeIds() {
      const ids = {}
      Object.keys(this.itemsBySection).forEach(k => {
        this.itemsBySection[k].forEach(it => { if (it.itemKind === 'Notice' && it.noticeId) ids[it.noticeId] = true })
      })
      return ids
    },
    // Live summary of the resulting form, in section order.
    assembled() {
      const steps = []
      this.groups.forEach(group => {
        const rows = this.itemsOf(group.key).map(it => ({
          key: it.uid,
          kind: it.itemKind,
          label: this.itemLabel(it),
          required: it.itemKind !== 'Notice' && !!it.required,
          note: it.note,
          muted: false,
        }))
        if (group.isDeclaration) {
          rows.push({ key: 'declaration', kind: 'Notice', label: 'Client Declaration', required: true, note: '', muted: true })
        }
        if (!rows.length) return
        steps.push({
          key: group.key,
          sectionKey: group.sectionKey,
          icon: group.key === UPLOADS_KEY ? 'FileTextIcon' : 'LayoutIcon',
          title: group.isDeclaration ? 'Review & sign' : group.title,
          rows,
        })
      })
      return steps
    },
  },

  methods: {
    kindBadge,
    kindLabel,
    ackLabel,

    // ---- open / load -------------------------------------------------------
    open(pkg) {
      const form = emptyForm()
      const items = {}
      if (pkg) {
        form.id = pkg.id
        form.name = pkg.Name || ''
        form.code = pkg.Code || ''
        form.description = pkg.Description || ''
        form.isDefault = !!pkg.IsDefault
        form.clientTypes = [...(pkg.ClientTypes || [])]
        form.accountTypes = [...(pkg.AccountTypes || [])]
        form.clientCategories = [...(pkg.ClientCategories || [])]
        form.sortOrder = Number(pkg.SortOrder) || 0
        form.appliedCount = pkg.AppliedCount || 0
        const sorted = [...(pkg.items || [])].sort((a, b) => (Number(a.SortOrder) || 0) - (Number(b.SortOrder) || 0))
        sorted.forEach(it => {
          const row = newRow(it.ItemKind || 'Upload')
          row.id = it.id || null
          row.questionId = it.QuestionId || null
          row.noticeId = it.NoticeId || null
          row.documentType = it.DocumentTypeId ? { id: it.DocumentTypeId, Name: it.DocumentTypeName } : null
          row.scope = it.Scope || (it.QuestionScope || 'Account')
          row.required = it.Required === undefined || it.Required === null ? true : !!it.Required
          row.note = it.Note || ''
          row.label = it.QuestionLabel || it.NoticeTitle || it.DocumentTypeName || ''
          row.meta = it.QuestionKey ? `${it.QuestionKey} · ${it.QuestionInputType || ''}` : ''
          const key = this.groupKeyFor(row, it)
          if (!items[key]) items[key] = []
          items[key].push(row)
        })
      }
      if (!pkg) {
        form.sortOrder = (this.data.packages || []).reduce((m, p) => Math.max(m, Number(p.SortOrder) || 0), 0) + 10
      }
      this.form = form
      this.itemsBySection = items
      this.picker = emptyPicker()
      this.showAssembled = false
      // open every group that has items (and the first one for a new package)
      const open = {}
      this.groups.forEach((g, i) => {
        open[g.key] = !!(items[g.key] && items[g.key].length) || (!pkg && i === 0)
      })
      this.openGroups = open
      this.show = true
    },

    // Where an existing package item belongs: its section, the person section of a person-scope
    // item, or the Documents pseudo group for untied account uploads.
    groupKeyFor(row, raw) {
      const explicit = raw.EffectiveSectionKey || raw.SectionKey
      if (explicit && this.groups.some(g => g.key === explicit)) return explicit
      if (row.itemKind === 'Question') {
        const q = this.questionsById[row.questionId]
        const scope = (q && q.Scope) || raw.QuestionScope
        if (scope && scope !== 'Account') {
          const person = this.groups.find(g => g.personType === scope)
          if (person) return person.key
        }
        return explicit || (q && q.DefaultSectionKey) || this.firstAccountGroupKey()
      }
      if (row.itemKind === 'Notice') {
        const n = this.noticesById[row.noticeId]
        return explicit || (n && n.DefaultSectionKey) || this.firstAccountGroupKey()
      }
      if (row.scope && row.scope !== 'Account') {
        const person = this.groups.find(g => g.personType === row.scope)
        if (person) return person.key
      }
      return UPLOADS_KEY
    },

    firstAccountGroupKey() {
      const g = this.groups.find(x => !x.personType && !x.isDeclaration && x.key !== UPLOADS_KEY)
      return g ? g.key : UPLOADS_KEY
    },

    // ---- groups ------------------------------------------------------------
    itemsOf(key) {
      return this.itemsBySection[key] || []
    },
    isOpen(key) {
      return !!this.openGroups[key]
    },
    toggleGroup(key) {
      this.$set(this.openGroups, key, !this.openGroups[key])
    },
    pushItem(key, row) {
      if (!this.itemsBySection[key]) this.$set(this.itemsBySection, key, [])
      this.itemsBySection[key].push(row)
      this.$set(this.openGroups, key, true)
    },
    removeItem(key, index) {
      this.itemsBySection[key].splice(index, 1)
    },
    moveItem(key, index, delta) {
      const list = this.itemsBySection[key]
      const target = index + delta
      if (!list || target < 0 || target >= list.length) return
      const copy = [...list]
      const [row] = copy.splice(index, 1)
      copy.splice(target, 0, row)
      this.$set(this.itemsBySection, key, copy)
    },

    // ---- labels ------------------------------------------------------------
    itemLabel(item) {
      if (item.itemKind === 'Question') {
        const q = this.questionsById[item.questionId]
        return (q && q.Label) || item.label || '(deleted question)'
      }
      if (item.itemKind === 'Notice') {
        const n = this.noticesById[item.noticeId]
        return (n && n.Title) || item.label || '(deleted notice)'
      }
      const d = item.documentType
      if (!d) return item.label || '(no document)'
      return typeof d === 'string' ? d : (d.Name || item.label || '')
    },
    itemMeta(item) {
      if (item.itemKind === 'Question') {
        const q = this.questionsById[item.questionId]
        return q ? `${q.Key} · ${q.InputType}` : item.meta
      }
      if (item.itemKind === 'Notice') {
        const n = this.noticesById[item.noticeId]
        return n ? ackLabel(n.AckMode) : ''
      }
      const parts = [scopeLabel(this.data.scopes, item.scope)]
      if (item.documentType && !item.documentType.id) parts.push('new document type')
      return parts.join(' · ')
    },

    // ---- pickers -----------------------------------------------------------
    isPicking(groupKey) {
      return this.picker.groupKey === groupKey
    },
    startPicker(group, kind) {
      const picker = emptyPicker()
      picker.groupKey = group.key
      picker.kind = kind
      picker.scope = group.personType || 'Account'
      this.picker = picker
      this.$set(this.openGroups, group.key, true)
    },
    cancelPicker() {
      this.picker = emptyPicker()
    },
    questionChoices(group) {
      const scope = group.personType || 'Account'
      const list = (this.data.questions || []).filter(q => q.Scope === scope && !this.usedQuestionIds[q.id])
      return [{ id: NEW_ID, Label: 'Create new question…' }].concat(list)
    },
    noticeChoices() {
      const list = (this.data.notices || []).filter(n => !n.IsSystem && !this.usedNoticeIds[n.id])
      return [{ id: NEW_ID, Title: 'Create new notice…' }].concat(list)
    },
    onPickQuestion(group, q) {
      if (!q) return
      if (q.id === NEW_ID) {
        this.picker.value = null
        const presets = group.personType
          ? { scope: group.personType }
          : { scope: 'Account', defaultSectionKey: group.sectionKey }
        this.$refs.questionModal.open(null, presets)
        return
      }
      this.addQuestion(group.key, q)
      this.picker = emptyPicker()
    },
    addQuestion(groupKey, q) {
      const row = newRow('Question')
      row.questionId = q.id
      row.scope = q.Scope || 'Account'
      row.required = q.DefaultRequired === undefined || q.DefaultRequired === null ? true : !!q.DefaultRequired
      row.label = q.Label || ''
      row.meta = `${q.Key || ''} · ${q.InputType || ''}`
      this.pushItem(groupKey, row)
    },
    onPickNotice(group, n) {
      if (!n) return
      if (n.id === NEW_ID) {
        this.picker.value = null
        this.$refs.noticeModal.open(null, { defaultSectionKey: group.sectionKey })
        return
      }
      this.addNotice(group.key, n)
      this.picker = emptyPicker()
    },
    addNotice(groupKey, n) {
      const row = newRow('Notice')
      row.noticeId = n.id
      row.required = false
      row.label = n.Title || ''
      this.pushItem(groupKey, row)
    },
    createDocumentTypeOption(name) {
      return { id: null, Name: String(name).trim() }
    },
    addUpload(group) {
      const resolved = this.resolveDocumentType(this.picker.documentType)
      if (!resolved) {
        this.$bvToast.toast('Pick a document type or type a new name.', { variant: 'danger', title: 'Validation', solid: true })
        return
      }
      const row = newRow('Upload')
      row.documentType = typeof this.picker.documentType === 'string'
        ? { id: null, Name: this.picker.documentType.trim() }
        : this.picker.documentType
      row.scope = group.personType || this.picker.scope || 'Account'
      row.required = !!this.picker.required
      row.label = row.documentType.Name
      this.pushItem(group.key, row)
      this.picker = emptyPicker()
    },

    // A question created inline lands in the group the picker was opened for.
    onQuestionCreated(question) {
      const groupKey = this.picker.groupKey
      this.$emit('refresh')
      if (question && question.id && groupKey) {
        this.addQuestion(groupKey, question)
      }
      this.picker = emptyPicker()
    },
    onNoticeCreated(notice) {
      const groupKey = this.picker.groupKey
      this.$emit('refresh')
      if (notice && notice.id && groupKey) {
        this.addNotice(groupKey, notice)
      }
      this.picker = emptyPicker()
    },

    // ---- save --------------------------------------------------------------
    resolveDocumentType(value) {
      if (!value) return null
      if (typeof value === 'string') {
        return value.trim() ? { documentTypeName: value.trim() } : null
      }
      if (value.id) return { documentTypeId: value.id }
      const name = (value.Name || '').trim()
      return name ? { documentTypeName: name } : null
    },

    buildItems() {
      const items = []
      const problems = []
      this.groups.forEach(group => {
        this.itemsOf(group.key).forEach(item => {
          const out = {
            id: item.id || undefined,
            itemKind: item.itemKind,
            required: item.itemKind === 'Notice' ? false : !!item.required,
            note: (item.note || '').trim() || null,
          }
          if (item.itemKind === 'Question') {
            if (!item.questionId) { problems.push(`${group.title}: a question item has no question.`); return }
            out.questionId = item.questionId
            const q = this.questionsById[item.questionId]
            const scope = (q && q.Scope) || item.scope || 'Account'
            if (scope === 'Account') out.sectionKey = group.sectionKey
          } else if (item.itemKind === 'Notice') {
            if (!item.noticeId) { problems.push(`${group.title}: a notice item has no notice.`); return }
            out.noticeId = item.noticeId
            out.sectionKey = group.sectionKey
          } else {
            const doc = this.resolveDocumentType(item.documentType)
            if (!doc) { problems.push(`${group.title}: an upload item has no document type.`); return }
            Object.assign(out, doc)
            out.scope = group.personType || item.scope || 'Account'
            if (!out.scope) { problems.push(`${group.title}: an upload item has no scope.`); return }
            if (group.sectionKey) out.sectionKey = group.sectionKey
          }
          items.push(out)
        })
      })
      return { items, problems }
    },

    handleSave(evt) {
      evt.preventDefault()
      if (!this.form.name.trim()) {
        this.$bvToast.toast('Package name is required.', { variant: 'danger', title: 'Validation', solid: true })
        return
      }
      const { items, problems } = this.buildItems()
      if (problems.length) {
        this.$bvToast.toast(problems[0], { variant: 'danger', title: 'Validation', solid: true })
        return
      }
      const f = this.form
      const payload = {
        id: f.id || undefined,
        name: f.name.trim(),
        code: f.code.trim() || undefined,
        description: f.description.trim() || null,
        isDefault: !!f.isDefault,
        clientTypes: f.isDefault ? f.clientTypes : [],
        accountTypes: f.isDefault ? f.accountTypes : [],
        clientCategories: f.isDefault ? f.clientCategories : [],
        sortOrder: Number(f.sortOrder) || 0,
        items,
        applyToExisting: !!f.id && !!f.applyToExisting,
        notify: !!f.id && !!f.applyToExisting && !!f.notify,
      }

      this.saving = true
      store.dispatch('settings/saveOnboardingPackage', payload)
        .then(res => {
          const affected = res.data.affectedOnboardings
          const suffix = affected ? ` ${affected} existing onboarding(s) updated.` : ''
          this.$bvToast.toast((res.data.message || 'Package saved.') + suffix, { variant: 'success', title: 'Success', solid: true })
          this.show = false
          this.$emit('saved', res.data.package || null)
        })
        .catch(err => {
          this.$bvToast.toast(apiError(err), { variant: 'danger', title: 'Error', solid: true })
        })
        .finally(() => { this.saving = false })
    },
  },
}
</script>

<style lang="scss">
.package-editor-dialog {
  .group-card {
    border: 1px solid #e6e8ee;
    border-radius: 0.428rem;
    background: #fff;
  }
  .group-card-declaration {
    background: #fafafa;
  }
  .group-head {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    padding: 0.5rem 0.75rem;
    cursor: pointer;
    user-select: none;
  }
  .group-actions {
    display: flex;
    flex-wrap: wrap;
    .btn {
      padding: 0.25rem 0.5rem;
      font-size: 0.8rem;
    }
  }
  .group-body {
    padding: 0.25rem 0.75rem 0.75rem;
    border-top: 1px solid #f0f1f5;
  }
  .item-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
    padding: 0.4rem 0;
    border-bottom: 1px dashed #eceef3;
    &:last-of-type {
      border-bottom: 0;
    }
  }
  .item-main {
    flex: 1 1 240px;
    min-width: 0;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  }
  .item-label {
    font-weight: 500;
  }
  .item-note {
    flex: 1 1 220px;
  }
  .item-required {
    flex: 0 0 auto;
    min-width: 90px;
  }
  .item-actions {
    flex: 0 0 auto;
  }
  .picker-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
    padding: 0.5rem;
    margin-top: 0.5rem;
    border: 1px dashed #b8c2d9;
    border-radius: 0.428rem;
    background: #f8f9fc;
  }
  .picker-select {
    flex: 1 1 260px;
    min-width: 220px;
  }
  .picker-scope {
    flex: 0 0 180px;
  }
  .picker-required {
    flex: 0 0 auto;
  }
  .assembled-panel {
    position: sticky;
    top: 0;
    max-height: 70vh;
    overflow: auto;
    padding: 0.75rem;
    border: 1px solid #e6e8ee;
    border-radius: 0.428rem;
    background: #f8f9fc;
  }
  .assembled-step {
    margin-bottom: 0.75rem;
  }
  .assembled-head {
    font-weight: 600;
    font-size: 0.85rem;
    margin-bottom: 0.25rem;
  }
  .assembled-items {
    list-style: none;
    padding-left: 0.25rem;
    margin: 0;
    font-size: 0.85rem;
    li {
      padding: 0.15rem 0;
    }
  }
  .assembled-kind {
    display: inline-block;
    width: 1.4rem;
    text-align: center;
    margin-right: 0.35rem;
    padding: 0.15rem 0;
  }
}
</style>
